/**
 * Per-pre-order hard lock.
 *
 * Aggregates every "this PO is mid-flight" signal into one boolean +
 * reason code so the rest of the recap can render the PO as fully
 * read-only while the lock is active. Two source classes:
 *
 *   1. **Local in-flight** (this tab) — a mutation we just dispatched
 *      hasn't returned yet. Sources:
 *        - `buffer.isFlushing` (flushModify / flushAndConfirm), passed
 *          in as a prop because each PreOrderSection already holds the
 *          buffer hook instance.
 *        - `isApprovingAi` / `isDecliningAi` from the **global**
 *          `aiInFlightStore`. NOT from a local `useAiPreOrderActions`
 *          call: that hook is also called inside `ModifyControls` (the
 *          component that actually fires approve), and React-local
 *          state from two different `useAiPreOrderActions(preOrderId)`
 *          calls does not sync — the lock would never see the
 *          ModifyControls' approve in flight. The shared store mirrors
 *          the hook's local state and any other entry point (e.g. the
 *          recap action bar's AI transitions) so every
 *          watcher of `preOrderId` sees the same truth.
 *
 *   2. **Server in-flight** (any tab, any user, the ConfirmConsumer is
 *      currently pushing to M3 — visible to all FE replicas via SSE).
 *      Source:
 *        - `pre_order_metadata.export.status === "in_progress"` — the
 *          export lock is held; any FE edit would race the M3 callback.
 *
 * A *failed* export is deliberately NOT a lock source. The export has
 * already finished, so there is no in-flight M3 callback to race — the
 * order must stay editable so the user can fix it and re-export (enter
 * modify mode → Save & Confirm, or the Retry button in
 * `ExportErrorPopover`). Only an `in_progress` export holds the lock.
 *
 * The lock is consumed by `PreOrderSection` to disable the cell table +
 * header inputs, and by `ModifyControls` to refuse a new Save / AI
 * accept while a previous one is still in flight.
 *
 * Race-safety rationale:
 *   - Backend already serializes concurrent approvals via
 *     `pg_advisory_xact_lock` (see `apps/api/src/domains/ai/pre-order/
 *     approval-service.ts`) and serializes confirms via the row-level
 *     FOR UPDATE lock in `confirm-service.ts`. Those guarantee
 *     correctness across concurrent calls. The FE lock is the
 *     **politeness layer** that prevents the user from FIRING the
 *     conflicting call in the first place — the backend won't lose
 *     data without the FE lock, but the user would see flaky
 *     race-y UX (a typed edit that survives one tab and dies in another,
 *     a Confirm that 409s with no explanation, etc.).
 *
 *   - The lock is read-only; computing it never triggers a backend
 *     call. Aggregation is pure over the inputs + the store snapshot.
 */

import { useMemo } from "react";
import type { PreOrder } from "@/app/api/types/preOrder";
import { useAiInFlightSlice } from "../stores/aiInFlightStore";
import { useConfirmInFlightSlice } from "../stores/confirmInFlightStore";

export type PreOrderLockReason =
  // Local — this tab is currently flushing buffered edits (typed cells
  // or AI promotions) via push-batch. Resolves when the round-trip
  // completes; the server may then transition into "exporting".
  | "savingEdits"
  // Local — this tab fired approveAll / declineAll on the AI suggestions.
  | "approvingAi"
  | "decliningAi"
  // Local — the recap action bar is mid-confirm or mid-retry. Covers
  // the gap between click and the server's SSE delivery of
  // `export.status="in_progress"`. Mirrored via `confirmInFlightStore`.
  | "confirming"
  // Server — the ConfirmConsumer holds the export lock (M3 push in flight).
  // Visible to every tab; clears via SSE when the export resolves.
  | "exporting";

export interface PreOrderLock {
  /** True when ANY of the source signals are active. */
  isLocked: boolean;
  /** First-matched reason, in priority order: local > server. */
  reason: PreOrderLockReason | null;
}

/** Inputs the hook needs. Kept as plain props so it stays testable. */
export interface UsePreOrderLockInput {
  /** The PO row (for `pre_order_metadata.export.status` + id lookup). */
  preOrder: Pick<PreOrder, "id" | "pre_order_metadata">;
  /** From `useModifyBuffer` — true while a push-batch round-trip is pending. */
  isFlushing: boolean;
}

export function usePreOrderLock({
  preOrder,
  isFlushing,
}: UsePreOrderLockInput): PreOrderLock {
  const exportStatus = preOrder.pre_order_metadata?.export?.status ?? null;
  const { isApprovingAi, isDecliningAi } = useAiInFlightSlice(preOrder.id);
  const { isConfirming } = useConfirmInFlightSlice(preOrder.id);
  return useMemo(() => {
    // Priority order: local in-flight signals win because they reflect
    // an action the user just took in THIS tab. The server signals are
    // ambient state — the user may not have caused them and shouldn't
    // see them framed as "your action is in progress".
    if (isFlushing) {
      return { isLocked: true, reason: "savingEdits" };
    }
    if (isApprovingAi) {
      return { isLocked: true, reason: "approvingAi" };
    }
    if (isDecliningAi) {
      return { isLocked: true, reason: "decliningAi" };
    }
    if (isConfirming) {
      return { isLocked: true, reason: "confirming" };
    }
    if (exportStatus === "in_progress") {
      return { isLocked: true, reason: "exporting" };
    }
    // A failed export does NOT lock — the order stays editable so the user
    // can fix it and re-export / retry. See the header doc for rationale.
    return { isLocked: false, reason: null };
  }, [exportStatus, isFlushing, isApprovingAi, isDecliningAi, isConfirming]);
}
